import type { OnboardingStatus } from "@xop-any-time/shared";

import { ApiError, api } from "./api.js";
import { env } from "./env.js";

/**
 * The onboarding wall's calls (spec E §9), in one typed place — the same
 * reason `crmApi.ts` exists for the portal's own screens.
 */

export type TemplateSlug = "reseller-agreement" | "reseller-nda";
export type SignedDocumentKind = "signed_agreement" | "signed_nda";

/** crm-api's per-file cap (spec E §13.2), mirrored so a wrong file costs a
 * click rather than an upload. The server's copy is the one that decides. */
export const MAX_PDF_BYTES = 10 * 1024 * 1024;

export const getOnboardingStatus = (): Promise<OnboardingStatus> =>
  api<OnboardingStatus>("/api/onboarding/status");

/** Public — a prospective partner may read the agreement before applying. */
export const templateUrl = (slug: TemplateSlug): string =>
  `${env.apiUrl}/api/onboarding/templates/${slug}`;

/** The applicant's own upload, back through this origin. */
export const signedDocumentUrl = (kind: SignedDocumentKind): string =>
  `${env.apiUrl}/api/onboarding/documents/${kind}`;

/**
 * Both signed PDFs, as ONE multipart request — the BFF forwards the envelope
 * to crm-api untouched, boundary included. Not through `api()`: that helper
 * JSON-encodes or sends an ArrayBuffer with a caller-set content type, and a
 * FormData body must be left to the browser, which writes the boundary into
 * the content-type header itself.
 */
export async function submitOnboardingDocuments(
  signedAgreement: File,
  signedNda: File,
): Promise<OnboardingStatus> {
  const form = new FormData();
  form.append("signed_agreement", signedAgreement, signedAgreement.name);
  form.append("signed_nda", signedNda, signedNda.name);

  const res = await fetch(`${env.apiUrl}/api/onboarding/documents`, {
    method: "POST",
    body: form,
    credentials: "include",
  });

  if (!res.ok) {
    let parsed: { message?: string; code?: string } = {};
    try {
      parsed = (await res.json()) as { message?: string; code?: string };
    } catch {
      // A proxy error page — keep the generic message.
    }
    throw new ApiError(
      parsed.message ?? `Upload failed with status ${res.status}`,
      res.status,
      parsed.code,
    );
  }
  return (await res.json()) as OnboardingStatus;
}

/** The client-side half of the PDF rule. Returns the sentence to show, or
 * null when the file may go. Extension is checked as well as type because
 * some browsers send an empty `type` for a file dragged from a mail client. */
export function checkPdf(file: File | null): string | null {
  if (!file) return "Choose a PDF to upload.";
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (!isPdf) return `${file.name} isn't a PDF. Upload the signed document as a PDF.`;
  if (file.size > MAX_PDF_BYTES) return `${file.name} is larger than 10 MB. Export a smaller PDF and try again.`;
  return null;
}
